import type { FrontPieceView, FrontRoomView } from '@obraplan/core/application/contracts/takeoff';
import type { PieceShape } from '@obraplan/core/domain/takeoff/piece-shape';
import type { Point } from '@obraplan/core/domain/takeoff/polygon';
import { progressFromRooms } from '@obraplan/core/domain/takeoff/room-progress';
import type { RoomWeightBasis } from '@obraplan/core/domain/takeoff/room-quantity';

/**
 * A planta pintada e a lista dos cômodos (Fase 34, etapa 2 — RN-441, RN-444, RN-448).
 *
 * Funções puras, fora dos componentes, para a recarga a quente e para o teste: o estado de cada
 * cômodo e de cada peça, os aptos do pavimento e o enquadramento da planta.
 */

/** O estado de um cômodo no serviço, que dá a cor da planta (os padrões do plano). */
export type EstadoDoComodo = 'NAO_INICIADO' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'NAO_SE_APLICA';

/** Cinza: 0% ou sem apontamento; amarelo: em andamento; verde: 100%; riscado: não se aplica. */
export function estadoDoComodo(room: {
  readonly notApplicable: boolean;
  readonly percent?: number;
}): EstadoDoComodo {
  if (room.notApplicable) return 'NAO_SE_APLICA';
  if (room.percent === undefined || room.percent <= 0) return 'NAO_INICIADO';
  return room.percent >= 100 ? 'CONCLUIDO' : 'EM_ANDAMENTO';
}

export const LEGENDA_DO_COMODO: Readonly<Record<EstadoDoComodo, string>> = {
  NAO_INICIADO: 'Não começado',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDO: 'Concluído',
  NAO_SE_APLICA: 'Não se aplica',
};

/** A peça da estrutura é pintada pela marca (RN-446): executada neste serviço, ou não. */
export type EstadoDaPeca = 'NAO_INICIADA' | 'EXECUTADA';

export function estadoDaPeca(piece: Pick<FrontPieceView, 'executedOn'>): EstadoDaPeca {
  return piece.executedOn === undefined ? 'NAO_INICIADA' : 'EXECUTADA';
}

/** Um apto do pavimento: a posição do desenho e os cômodos da tipologia dela (RN-441). */
export interface AptoDaFrente {
  readonly positionId: string;
  readonly label: string;
  readonly count: number;
  readonly unitTypeId: string;
  readonly unitTypeName: string;
  readonly rooms: readonly FrontRoomView[];
}

/** Os aptos na ordem em que o servidor os lista (a ordem do desenho), cada um com os cômodos. */
export function aptosDaFrente(rooms: readonly FrontRoomView[]): readonly AptoDaFrente[] {
  const aptos = new Map<string, { apto: AptoDaFrente; rooms: FrontRoomView[] }>();
  for (const room of rooms) {
    let atual = aptos.get(room.positionId);
    if (atual === undefined) {
      const lista: FrontRoomView[] = [];
      atual = {
        apto: {
          positionId: room.positionId,
          label: room.positionLabel,
          count: room.positionCount,
          unitTypeId: room.unitTypeId,
          unitTypeName: room.unitTypeName,
          rooms: lista,
        },
        rooms: lista,
      };
      aptos.set(room.positionId, atual);
    }
    atual.rooms.push(room);
  }
  return [...aptos.values()].map((item) => item.apto);
}

/**
 * "Apto 01", "Área comum": o nome do apto na tela. O rótulo que já diz o que é (área comum, hall,
 * vagas) fica como está; o número ganha o "Apto".
 */
export function nomeDoApto(label: string): string {
  return /^\d+[A-Za-z]?$/.test(label.trim()) ? `Apto ${label.trim()}` : label;
}

/**
 * A mesma tipologia em duas posições tem um contorno só: o desenho não guarda onde cada apto
 * fica, só a tipologia traçada sobre a prancha. A planta pinta, de cada tipologia, **um** apto —
 * o escolhido, ou o primeiro —, e a tela oferece a troca quando há mais de um.
 */
export function aptosDaPlanta(
  aptos: readonly AptoDaFrente[],
  escolhidos: Readonly<Record<string, string>>,
): readonly AptoDaFrente[] {
  const porTipologia = new Map<string, AptoDaFrente[]>();
  for (const apto of aptos) {
    const lista = porTipologia.get(apto.unitTypeId) ?? [];
    lista.push(apto);
    porTipologia.set(apto.unitTypeId, lista);
  }
  return [...porTipologia.entries()].map(([unitTypeId, lista]) => {
    const escolhido = escolhidos[unitTypeId];
    return lista.find((apto) => apto.positionId === escolhido) ?? lista[0]!;
  });
}

/** As tipologias com mais de um apto no pavimento: as que pedem a escolha de qual pintar. */
export function tipologiasRepetidas(
  aptos: readonly AptoDaFrente[],
): readonly { readonly unitTypeId: string; readonly aptos: readonly AptoDaFrente[] }[] {
  const porTipologia = new Map<string, AptoDaFrente[]>();
  for (const apto of aptos) {
    const lista = porTipologia.get(apto.unitTypeId) ?? [];
    lista.push(apto);
    porTipologia.set(apto.unitTypeId, lista);
  }
  return [...porTipologia.entries()]
    .filter(([, lista]) => lista.length > 1)
    .map(([unitTypeId, lista]) => ({ unitTypeId, aptos: lista }));
}

/** Os pontos que a figura da peça ocupa — para o enquadramento. */
function pontosDaForma(forma: PieceShape): readonly Point[] {
  switch (forma.kind) {
    case 'CIRCULO':
      return [
        { x: forma.center.x - forma.radius, y: forma.center.y - forma.radius },
        { x: forma.center.x + forma.radius, y: forma.center.y + forma.radius },
      ];
    case 'POLIGONOS':
      return forma.outlines.flat();
    case 'LINHA':
      return [forma.from, forma.to];
    case 'PONTO':
      return [forma.at];
  }
}

export interface Enquadramento {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

// ── O zoom (23/10/2026) ─────────────────────────────────────────────────────────

/** A caixa do `svg` na tela — o que o `getBoundingClientRect` dá. */
export interface CaixaNaTela {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Pixels de tela por metro da prancha, com o `xMidYMid meet` do `svg`. */
export function escalaNaTela(vista: Enquadramento, caixa: CaixaNaTela): number {
  return Math.min(caixa.width / vista.width, caixa.height / vista.height);
}

/** O ponto da prancha sob um ponto da tela. */
export function naTela(vista: Enquadramento, tela: Point, caixa: CaixaNaTela): Point {
  const escala = escalaNaTela(vista, caixa);
  const origemX = caixa.left + (caixa.width - vista.width * escala) / 2;
  const origemY = caixa.top + (caixa.height - vista.height * escala) / 2;
  return { x: vista.x + (tela.x - origemX) / escala, y: vista.y + (tela.y - origemY) / escala };
}

/** A vista do tamanho dado que põe o ponto `ancora` da prancha sob o ponto `tela`. */
export function ancorar(
  tamanho: { readonly width: number; readonly height: number },
  ancora: Point,
  tela: Point,
  caixa: CaixaNaTela,
): Enquadramento {
  const escala = Math.min(caixa.width / tamanho.width, caixa.height / tamanho.height);
  const origemX = caixa.left + (caixa.width - tamanho.width * escala) / 2;
  const origemY = caixa.top + (caixa.height - tamanho.height * escala) / 2;
  return {
    x: ancora.x - (tela.x - origemX) / escala,
    y: ancora.y - (tela.y - origemY) / escala,
    width: tamanho.width,
    height: tamanho.height,
  };
}

/**
 * Amplia (`fator` < 1) ou reduz (> 1) a vista mantendo parado o ponto da prancha que está sob
 * `tela` — o mouse, ou o meio da planta pelos botões.
 */
export function ampliar(
  vista: Enquadramento,
  fator: number,
  tela: Point,
  caixa: CaixaNaTela,
): Enquadramento {
  return ancorar(
    { width: vista.width * fator, height: vista.height * fator },
    naTela(vista, tela, caixa),
    tela,
    caixa,
  );
}

/**
 * A vista dentro do razoável: no máximo 60 vezes o quadro, no mínimo o quadro ou a prancha
 * inteira, e o centro sem sair do que se desenha. A proporção da vista não muda.
 */
export function limitar(
  vista: Enquadramento,
  quadro: Enquadramento,
  fundo?: { readonly width: number; readonly height: number },
): Enquadramento {
  const total =
    fundo === undefined
      ? quadro
      : {
          x: Math.min(quadro.x, 0),
          y: Math.min(quadro.y, 0),
          width: Math.max(quadro.x + quadro.width, fundo.width) - Math.min(quadro.x, 0),
          height: Math.max(quadro.y + quadro.height, fundo.height) - Math.min(quadro.y, 0),
        };
  const maior = Math.max(total.width / vista.width, total.height / vista.height);
  const menor = Math.max(quadro.width, quadro.height) / 60;
  let fator = 1;
  if (maior < 1) fator = maior; // larga demais: volta ao todo
  if (Math.max(vista.width, vista.height) * fator < menor) {
    fator = menor / Math.max(vista.width, vista.height);
  }
  const width = vista.width * fator;
  const height = vista.height * fator;
  const cx = Math.min(Math.max(vista.x + vista.width / 2, total.x), total.x + total.width);
  const cy = Math.min(Math.max(vista.y + vista.height / 2, total.y), total.y + total.height);
  return { x: cx - width / 2, y: cy - height / 2, width, height };
}

/**
 * O retângulo que cabe tudo o que se desenha, com uma folga de 4% do lado maior (no mínimo
 * 0,5 m). `undefined` quando não há o que desenhar: nenhum cômodo traçado, nenhuma peça no lugar.
 */
export function enquadrar(
  contornos: readonly (readonly Point[])[],
  formas: readonly PieceShape[] = [],
): Enquadramento | undefined {
  const pontos = [...contornos.flat(), ...formas.flatMap(pontosDaForma)];
  if (pontos.length === 0) return undefined;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ponto of pontos) {
    minX = Math.min(minX, ponto.x);
    minY = Math.min(minY, ponto.y);
    maxX = Math.max(maxX, ponto.x);
    maxY = Math.max(maxY, ponto.y);
  }
  const folga = Math.max(0.5, Math.max(maxX - minX, maxY - minY) * 0.04);
  return {
    x: minX - folga,
    y: minY - folga,
    width: maxX - minX + 2 * folga,
    height: maxY - minY + 2 * folga,
  };
}

/** O centro de massa do contorno — onde vai o nome e o percentual do cômodo. */
export function centroDoContorno(contorno: readonly Point[]): Point {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < contorno.length; i += 1) {
    const a = contorno[i]!;
    const b = contorno[(i + 1) % contorno.length]!;
    const cruz = a.x * b.y - b.x * a.y;
    area += cruz;
    cx += (a.x + b.x) * cruz;
    cy += (a.y + b.y) * cruz;
  }
  if (Math.abs(area) < 1e-9) {
    // Contorno degenerado: a média dos pontos basta para pôr o rótulo.
    const n = contorno.length;
    return {
      x: contorno.reduce((soma, p) => soma + p.x, 0) / n,
      y: contorno.reduce((soma, p) => soma + p.y, 0) / n,
    };
  }
  return { x: cx / (3 * area), y: cy / (3 * area) };
}

/** A chave de um cômodo de um apto: a mesma do servidor (posição × cômodo). */
export function chaveDoComodo(room: {
  readonly positionId: string;
  readonly roomId: string;
}): string {
  return `${room.positionId}|${room.roomId}`;
}

/** Quantas peças a marca já executou, para o resumo do item. */
export function resumoDasPecas(pieces: readonly FrontPieceView[]): {
  readonly total: number;
  readonly executadas: number;
  readonly naPlanta: number;
} {
  return {
    total: pieces.length,
    executadas: pieces.filter((peca) => peca.executedOn !== undefined).length,
    naPlanta: pieces.filter((peca) => peca.shape !== undefined).length,
  };
}

/** `72.5` vira `"72,5%"`; `100` vira `"100%"`. */
export function percentual(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
}

/** O ponto que representa a figura da peça: é ele que o retângulo de escolha precisa conter. */
export function ancoraDaForma(forma: PieceShape): Point {
  switch (forma.kind) {
    case 'CIRCULO':
      return forma.center;
    case 'PONTO':
      return forma.at;
    case 'POLIGONOS':
      return forma.outlines[0] === undefined ? { x: 0, y: 0 } : centroDoContorno(forma.outlines[0]);
    case 'LINHA':
      return { x: (forma.from.x + forma.to.x) / 2, y: (forma.from.y + forma.to.y) / 2 };
  }
}

/** O ponto está no retângulo de `a` a `b`, qualquer que seja o canto em que o arrasto começou. */
export function dentroDoRetangulo(ponto: Point, a: Point, b: Point): boolean {
  return (
    ponto.x >= Math.min(a.x, b.x) &&
    ponto.x <= Math.max(a.x, b.x) &&
    ponto.y >= Math.min(a.y, b.y) &&
    ponto.y <= Math.max(a.y, b.y)
  );
}

/** A chave de escolha de uma peça na planta. */
export function chaveDaPeca(piece: { readonly name: string }): string {
  return `peca:${piece.name}`;
}

/**
 * O avanço de um grupo de cômodos — o apto, o pavimento —, pesado como o servidor pesa o item
 * (RN-445): pela medida de cada cômodo, ou igual quando o desenho não mede o serviço por
 * cômodo. O "não se aplica" fica de fora da conta. `undefined` sem cômodo que conte.
 */
export function avancoDosComodos(
  rooms: readonly FrontRoomView[],
  basis: RoomWeightBasis | undefined,
): number | undefined {
  return progressFromRooms(
    rooms
      .filter((room) => !room.notApplicable)
      .map((room) => ({
        weight: basis === 'IGUAL' ? 1 : (room.measure ?? 1),
        ...(room.percent === undefined ? {} : { percent: room.percent }),
      })),
  );
}
